/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from "react";
import {
  Message,
  TextArea,
  InputField,
  MyButton,
  AddPageShell,
  AddSection,
} from "../components/Component";
import { FOLDERS } from "../doseeData/utils/Utils";
import { codeOk, errorMessage } from "../../lib/index";
import { useBlogContext } from "../../context/BlogContext";
import { getOneBlog } from "../../lib/sysNavigation/SysNavigation";
import { defaultImage } from "../../lib/libraries/imageKit/ImageKit";
import { ImageKitUpload } from "../../ui/ImageKitLibrary";
import GenerateDescription from "../doseeData/utils/GenerateDescription";
import { FiFileText, FiHash, FiImage, FiAlignLeft } from "react-icons/fi";
import { FaHandSparkles } from "react-icons/fa";

const Edit = () => {
  const { blog, fetchOneBlog, updateBlog, updateBlogState } = useBlogContext();

  const [localBlog, setLocalBlog] = useState(getOneBlog());

  const blog_id = localBlog.blog_id;
  const [error, setError] = useState(false);
  const [status, setStatus] = useState(0);
  const [message, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [thumbnail, setThumbnail] = useState(localBlog.thumbnail);
  const [thumbnailId, setThumbnailId] = useState(localBlog.image_cdn_id);
  const [title, setTitle] = useState(localBlog.title);
  const [description, setDescription] = useState(localBlog.description);
  const [author, setAuthor] = useState(localBlog.author);
  const localBlogRoutes = "/blog";
  const [showDescriptionModal, setShowDescriptionModal] = useState(null);

  const slugPreview = useMemo(
    () => (title ? String(title).toLowerCase().replace(/\s/g, "_") : "—"),
    [title],
  );

  const descMeta = useMemo(() => {
    const text = description || "";
    const words = text.trim() ? text.trim().split(/\s+/).length : 0;
    const chars = text.length;
    return { words, chars };
  }, [description]);

  const cleanFields = () => {
    setLoading(false);
    setSuccess(true);
  };

  const responses = (isError: any, st: any, msg: any) => {
    setError(isError);
    setStatus(st);
    setErrorMessage(msg);
  };

  useEffect(() => {
    if (!blog) fetchOneBlog(blog_id);
    else setLocalBlog(blog);
  }, [blog]);

  const updateOneBlogHandler = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    if (!thumbnail || !thumbnailId) {
      setLoading(false);
      return responses(true, 400, "Image Required");
    } else {
      await sendDBRequest();
    }
  };

  const sendDBRequest = async () => {
    const data = {
      title: title,
      author: author,
      reading_time: 10,
      description: description,
      slug: title?.toLowerCase().replace(/\s/g, "_"),
      thumbnail: thumbnail ? thumbnail : defaultImage,
      image_cdn_id: thumbnailId ? thumbnailId : "...",
    };

    const response = await updateBlog(blog_id, data);
    if (response) {
      if (response.data.status === codeOk) {
        updateBlogState({ blog_id, ...data });
        cleanFields();
        return responses(true, response.data.status, response.data.message);
      } else {
        setLoading(false);
        return responses(true, response.data.status, response.data.message);
      }
    } else {
      setLoading(false);
      return responses(true, 400, errorMessage);
    }
  };

  return (
    <AddPageShell
      backRoute={localBlogRoutes}
      eyebrow="Content • Blog"
      title="Edit Blog Post"
      subtitle="Update editorial content with hero image, byline and rich body."
      icon={<FiFileText className="h-5 w-5" />}
      pills={[
        { label: "Words", value: descMeta.words },
        { label: "Chars", value: descMeta.chars },
      ]}
      modals={
        showDescriptionModal ? (
          <GenerateDescription
            setDescription={setDescription}
            setShowDescriptionModal={setShowDescriptionModal}
          />
        ) : null
      }
      loading={loading}
      submitLabel="Save Changes"
      onSubmit={updateOneBlogHandler}
      onSubmitClick={() => {
        setError(false);
        setSuccess(false);
        setErrorMessage("");
      }}
      message={
        <Message
          props={{
            error,
            success,
            message,
            loading,
            status,
            onClick: () => {
              setError(false);
              setSuccess(false);
              setErrorMessage("");
            },
          }}
        />
      }
    >
      <AddSection
        title="Cover Image"
        hint="1600×900 recommended. JPG or PNG."
        icon={<FiImage className="h-4 w-4" />}
      >
        <ImageKitUpload
          thumbnail={thumbnail}
          thumbnailId={thumbnailId}
          setThumbnail={setThumbnail}
          setThumbnailId={setThumbnailId}
          folder={FOLDERS.TOUR_PACKAGES}
        />
      </AddSection>

      <AddSection
        title="Article Basics"
        hint="Title becomes the slug used in URLs."
        icon={<FiFileText className="h-4 w-4" />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <InputField
              props={{
                id: "title",
                title: "Blog Title",
                type: "text",
                required: true,
                value: title ? title : "",
                onChange: (e: any) => {
                  setTitle(e.target.value);
                  setError(null);
                  setSuccess(null);
                },
              }}
            />
            <p className="inline-flex items-center gap-1.5 text-[11px] text-gray-500">
              <FiHash className="h-3 w-3" />
              Slug: <span className="font-mono">{slugPreview}</span>
            </p>
          </div>
          <InputField
            props={{
              id: "author",
              title: "Author",
              type: "text",
              value: author ? author : "",
              onChange: (e: any) => {
                setAuthor(e.target.value);
                setError(null);
                setSuccess(null);
              },
            }}
          />
        </div>
      </AddSection>

      <AddSection
        title="Body"
        hint="Use the editor toolbar to format. You can refine after saving."
        icon={<FiAlignLeft className="h-4 w-4" />}
        actions={
          <MyButton
            onClick={() => setShowDescriptionModal(true)}
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-br from-brand to-primary px-3 py-2 text-xs font-semibold text-white shadow hover:opacity-95"
          >
            <FaHandSparkles className="h-4 w-4" />
            Generate
          </MyButton>
        }
      >
        <TextArea
          props={{
            id: "description",
            title: "Description",
            type: "text",
            value: description ? description : "",
            onChange: (e: any) => {
              setDescription(e.target.value);
              setError(null);
              setSuccess(null);
            },
          }}
        />
      </AddSection>
    </AddPageShell>
  );
};

export default Edit;
